import type { credentials } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * Pure presentation + form logic for the Credentials screens.
 *
 * Same discipline as `supervisionLogic.ts`: status is a SERVER verdict
 * (`apps/api/src/domains/credentials/status.ts`), never re-derived here. This
 * file only decides how it reads.
 */

export const STATUS_VARIANT: Record<credentials.CredentialStatus, Severity> = {
  active: 'good',
  expiring_soon: 'look',
  expired: 'urgent',
  pending_verification: 'unsure',
  rejected: 'urgent',
  missing: 'urgent',
  superseded: 'plain',
};

/**
 * Fallback labels for shapes that carry no `statusLabel` of their own.
 *
 * THE SHAPE THIS EXISTED FOR NO LONGER NEEDS IT. `WorkerCredentialLine` was
 * derived read-side and carried `status` alone, so this map decided what every
 * line in the per-type table read as — including `missing`, which is derived
 * rather than stored and therefore has no `CredentialView` to borrow copy from.
 * The line now carries `statusLabel`/`statusMeaning` straight from
 * `CREDENTIAL_STATUS_COPY`, and `statusPill` below already prefers a
 * server-sent label, so the server's words win for that shape without this map
 * being consulted at all.
 *
 * It is kept because `statusPill` takes a STRUCTURAL argument — `{ status,
 * statusLabel? }` — rather than a `WorkerCredentialLine`, so a caller passing a
 * bare status still reaches it. It still mirrors `CREDENTIAL_STATUS_COPY` in
 * `apps/api/src/domains/credentials/status.ts` verbatim, and a hand-maintained
 * mirror is worth removing entirely once nothing reaches it.
 */
const STATUS_LABELS: Record<credentials.CredentialStatus, string> = {
  active: 'Active',
  expiring_soon: 'Expiring soon',
  expired: 'Expired',
  pending_verification: 'Pending verification',
  rejected: 'Needs correction',
  missing: 'Missing',
  superseded: 'Replaced',
};

export function statusPill(row: { status: credentials.CredentialStatus; statusLabel?: string }): {
  label: string;
  variant: Severity;
} {
  return {
    label: row.statusLabel ?? STATUS_LABELS[row.status],
    variant: STATUS_VARIANT[row.status],
  };
}

export const STATUS_TABS: Array<{ key: 'all' | credentials.CredentialStatus; label: string }> = [
  { key: 'all', label: 'Everyone' },
  { key: 'expired', label: 'Expired' },
  { key: 'missing', label: 'Missing' },
  { key: 'rejected', label: 'Needs correction' },
  { key: 'pending_verification', label: 'Pending verification' },
  { key: 'expiring_soon', label: 'Expiring soon' },
  { key: 'active', label: 'Active' },
];

export const CATEGORY_LABELS: Record<credentials.CredentialTypeView['category'], string> = {
  license: 'License',
  certification: 'Certification',
  training: 'Training',
  clearance: 'Clearance',
  other: 'Other',
};

/**
 * Whether a workforce row belongs on a given filter tab — the row's WORST line
 * decides membership, mirroring how the server picks the worst policy for
 * supervision. `all` always matches; every other tab needs at least one line
 * carrying that exact status.
 */
export function rowMatchesTab(
  row: credentials.WorkforceCredentialRow,
  tab: 'all' | credentials.CredentialStatus,
): boolean {
  if (tab === 'all') return true;
  return row.lines.some((l) => l.status === tab);
}

/**
 * The one status a workforce list row shows in its summary column: worst
 * first, same ranking `status.ts` uses to pick a worker's best-of-type line,
 * inverted here to pick the WORST across all their lines.
 */
const SEVERITY_RANK: Record<credentials.CredentialStatus, number> = {
  expired: 6,
  missing: 5,
  rejected: 4,
  pending_verification: 3,
  expiring_soon: 2,
  active: 1,
  superseded: 0,
};

export function worstLine(
  row: credentials.WorkforceCredentialRow,
): credentials.WorkerCredentialLine | null {
  const required = row.lines.filter((l) => l.required);
  const pool = required.length > 0 ? required : row.lines;
  if (pool.length === 0) return null;
  return [...pool].sort((a, b) => SEVERITY_RANK[b.status] - SEVERITY_RANK[a.status])[0]!;
}

/** See `supervisionLogic.formatCalendarDate` for why UTC is pinned here too. */
export function formatCalendarDate(date: string | null, locale?: string): string {
  if (!date) return '—';
  const d = new Date(`${date}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function expiryLabel(expiresOn: string | null, daysUntilExpiry: number | null): string {
  if (!expiresOn) return 'No expiry';
  const date = formatCalendarDate(expiresOn);
  if (daysUntilExpiry === null) return date;
  if (daysUntilExpiry < 0) return `${date} (expired)`;
  if (daysUntilExpiry === 0) return `${date} (today)`;
  return `${date} (${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'})`;
}

// --- Credential type draft -----------------------------------------------------

export interface TypeDraft {
  name: string;
  description: string;
  category: credentials.CredentialTypeView['category'];
  issuingAuthorityLabel: string;
  requiresExpiration: boolean;
  requiresCredentialNumber: boolean;
  expiringSoonDays: string;
  renewalMonths: string;
}

export const EMPTY_TYPE_DRAFT: TypeDraft = {
  name: '',
  description: '',
  category: 'other',
  issuingAuthorityLabel: '',
  requiresExpiration: true,
  requiresCredentialNumber: false,
  expiringSoonDays: '60',
  renewalMonths: '',
};

export function validateTypeDraft(draft: TypeDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  if (draft.name.trim().length === 0) errors['name'] = 'A name is required.';
  const days = Number(draft.expiringSoonDays);
  if (!Number.isInteger(days) || days < 0 || days > 730) {
    errors['expiringSoonDays'] = 'Enter a whole number of days, 0–730.';
  }
  if (draft.renewalMonths.trim() !== '') {
    const months = Number(draft.renewalMonths);
    if (!Number.isInteger(months) || months < 1 || months > 240) {
      errors['renewalMonths'] = 'Enter a whole number of months, 1–240, or leave it blank.';
    }
  }
  return errors;
}

export function typePayload(draft: TypeDraft): credentials.CreateTypeInput {
  return {
    name: draft.name.trim(),
    description: draft.description.trim() || undefined,
    category: draft.category,
    issuingAuthorityLabel: draft.issuingAuthorityLabel.trim() || undefined,
    requiresExpiration: draft.requiresExpiration,
    requiresCredentialNumber: draft.requiresCredentialNumber,
    expiringSoonDays: Number(draft.expiringSoonDays),
    renewalMonths: draft.renewalMonths.trim() === '' ? null : Number(draft.renewalMonths),
  };
}

// --- Requirement draft -----------------------------------------------------------

export interface RequirementDraft {
  credentialTypeId: string;
  appliesToRoleKey: string;
  appliesToProgramId: string;
  appliesToEmploymentStatus: '' | 'active' | 'inactive' | 'terminated';
  effectiveFrom: string;
  effectiveTo: string;
}

export const EMPTY_REQUIREMENT_DRAFT: RequirementDraft = {
  credentialTypeId: '',
  appliesToRoleKey: '',
  appliesToProgramId: '',
  appliesToEmploymentStatus: '',
  effectiveFrom: '',
  effectiveTo: '',
};

export function validateRequirementDraft(
  draft: RequirementDraft,
  today: string,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (draft.credentialTypeId.trim() === '')
    errors['credentialTypeId'] = 'Choose a credential type.';
  if (draft.effectiveFrom.trim() === '') {
    errors['effectiveFrom'] = 'Choose when this takes effect.';
  } else if (draft.effectiveFrom < today) {
    errors['effectiveFrom'] = 'This cannot take effect in the past.';
  }
  if (draft.effectiveTo && draft.effectiveTo < draft.effectiveFrom) {
    errors['effectiveTo'] = 'The end date cannot be before the start date.';
  }
  return errors;
}

/**
 * Pre-fills the amend form from the requirement being amended. `effectiveFrom`
 * is left blank rather than copied — the server requires the new window to
 * start strictly AFTER the current one, so today's date is never a safe
 * default; the person has to choose a real date going forward.
 */
export function draftFromRequirement(r: credentials.CredentialRequirementView): RequirementDraft {
  return {
    credentialTypeId: r.credentialTypeId,
    appliesToRoleKey: r.appliesToRoleKey ?? '',
    appliesToProgramId: r.appliesToProgramId ?? '',
    appliesToEmploymentStatus: (r.appliesToEmploymentStatus ??
      '') as RequirementDraft['appliesToEmploymentStatus'],
    effectiveFrom: '',
    effectiveTo: '',
  };
}

export function requirementPayload(draft: RequirementDraft): credentials.CreateRequirementInput {
  return {
    credentialTypeId: draft.credentialTypeId,
    appliesToRoleKey: draft.appliesToRoleKey.trim() || null,
    appliesToProgramId: draft.appliesToProgramId.trim() || null,
    appliesToEmploymentStatus: draft.appliesToEmploymentStatus || null,
    effectiveFrom: draft.effectiveFrom,
    effectiveTo: draft.effectiveTo.trim() || null,
  };
}

// --- File / renew draft ----------------------------------------------------------

export interface FileDraft {
  credentialTypeId: string;
  credentialNumber: string;
  issuingAuthority: string;
  issuedOn: string;
  expiresOn: string;
}

export const EMPTY_FILE_DRAFT: FileDraft = {
  credentialTypeId: '',
  credentialNumber: '',
  issuingAuthority: '',
  issuedOn: '',
  expiresOn: '',
};

/**
 * `type` is optional so the form can validate before its type is even chosen;
 * once chosen, the type's own requirements (an expiry, a credential number)
 * apply — the same rule `fileCredential` enforces server-side.
 */
export function validateFileDraft(
  draft: FileDraft,
  type: credentials.CredentialTypeView | undefined,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (draft.credentialTypeId.trim() === '')
    errors['credentialTypeId'] = 'Choose a credential type.';
  if (type?.requiresExpiration && draft.expiresOn.trim() === '') {
    errors['expiresOn'] = `${type.name} needs an expiry date.`;
  }
  if (type?.requiresCredentialNumber && draft.credentialNumber.trim() === '') {
    errors['credentialNumber'] = `${type.name} needs a credential number.`;
  }
  if (draft.issuedOn && draft.expiresOn && draft.expiresOn < draft.issuedOn) {
    errors['expiresOn'] = 'The expiry date cannot be before the issue date.';
  }
  return errors;
}

export function filePayload(workerId: string, draft: FileDraft): credentials.FileCredentialInput {
  return {
    workerId,
    credentialTypeId: draft.credentialTypeId,
    credentialNumber: draft.credentialNumber.trim() || undefined,
    issuingAuthority: draft.issuingAuthority.trim() || undefined,
    issuedOn: draft.issuedOn.trim() || undefined,
    expiresOn: draft.expiresOn.trim() || undefined,
  };
}

export function renewPayload(draft: FileDraft): credentials.RenewCredentialInput {
  return {
    credentialNumber: draft.credentialNumber.trim() || undefined,
    issuingAuthority: draft.issuingAuthority.trim() || undefined,
    issuedOn: draft.issuedOn.trim() || undefined,
    expiresOn: draft.expiresOn.trim() || undefined,
  };
}

/** The server's message when it has one. */
export function messageOf(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return 'That did not go through.';
}
